import { useMemo } from 'react'
import { cellWallPath, generateLogoMaze, strokePath } from '@/lib/logoMaze'
import './LogoMaze.css'

type Props = { seed?: number; margin?: number; className?: string }

/**
 * The hero artwork: a maze that fills in cell by cell, growing out of the
 * Softmaze mark, then the route through it is traced in the accent colour
 * and the logo appears. Pure SVG; walls inherit currentColor.
 */
export default function LogoMaze({ seed = 1150, margin = 1, className }: Props) {
  const size = 24
  const { maze, cells, trace, w, h, top, left } = useMemo(() => {
    const m = generateLogoMaze(margin, seed)
    const cells = Array.from({ length: m.cols * m.rows }, (_, i) => ({
      d: cellWallPath(m, i, size),
      delay: m.order[i] / m.maxOrder,
    })).filter((c) => c.d)
    return {
      maze: m,
      cells,
      trace: strokePath(m.trace, size),
      w: m.cols * size,
      h: m.rows * size,
      top: `M0 0H${m.cols * size}`,
      left: `M0 0V${m.rows * size}`,
    }
  }, [seed, margin])

  return (
    <svg
      className={`logo-maze ${className ?? ''}`}
      viewBox={`-2 -2 ${w + 4} ${h + 4}`}
      role="img"
      aria-label="The Softmaze mark forming as the route through a maze"
      shapeRendering="crispEdges"
    >
      <defs>
        <linearGradient id="logo-maze-grad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={w} y2={h}>
          <stop offset="0" className="logo-maze__stop-a" />
          <stop offset="1" className="logo-maze__stop-b" />
        </linearGradient>
      </defs>
      <g className="logo-maze__walls">
        <path d={top} pathLength={1} style={{ ['--t' as string]: 0 }} />
        <path d={left} pathLength={1} style={{ ['--t' as string]: 0 }} />
        {cells.map((c, i) => (
          <path key={i} d={c.d} pathLength={1} style={{ ['--t' as string]: c.delay }} />
        ))}
      </g>
      <g className="logo-maze__glyph">
        <path d={trace} pathLength={1} />
      </g>
      <title>{`${maze.cols}×${maze.rows} maze`}</title>
    </svg>
  )
}
